import type { InstantConfigRepository } from "instant-config";
import type { PageTargeting } from "../../../../config/page";

/**
 * Configuration properties that Double Verify will use from Instant Config.
 *
 * Double Verify currently uses `icDoubleVerify` boolean to enable to disable the plugin.
 */
export type DoubleVerifyPluginConfig = Partial<
  Pick<InstantConfigRepository, "icDoubleVerify">
>;

/**
 * Initial value to use for page and slot targeting values
 */
export const DEFAULT_TARGETING_VALUE = "-1";

/**
 * For the VLP and TVP response objects that set the targeting for each slot, the request to
 * Double Verify has a query param in the shape of `adunits[<ad unit path>][<?>]=<key?>`.
 *
 * This query param is expected for each ad unit path, which for us since slotId is in the path, is
 * for each slot.
 *
 * There are 3 strings passed into the query param, and for us now only one is used.
 * - `<ad unit path>`: This is the full ad unit path and must match the ad unit path for the desired
 *   slots registered in the googletag defineslot method.
 * - `<?>`: Currently unknown what this second array inner value is for, but it's currently unused
 *   and passing anything in there will make the slot responses disappear. We are working on getting
 *   documentation from DoubleVerify to find out what this value is.
 * - `<key?>`: This value is also somewhat unknown, but when set it is returned as the key for the
 *    string value in the response, the `""` in the sub object here. If passed in for example as
 *    "somekey", ie: `adunits[/path][]=somekey`, then the response is
 *    `{ "/path": { "somkey": "value" } }`. When empty, as in `adunits[/path][]=`, we get back
 *    `{ "/path": { "": "value" } }`. This empty value is the current implementation in AE3 so we
 *    will continue to use that until we get documentation and validate how this key is used and if
 *    anything can be passed in or if it's a specific key.
 */
export type DoubleVerifySlotResponseMap = {
  [adUnitPath: string]: {
    "": string;
  };
};

/**
 * The response object from double verify.
 *
 * TVP and VLP are only returned when DoubleVerify was provided the
 * `adunits[<ad unit path>][]=` query param and the ad unit path provided is a known path to
 * double verify. Otherwise that ad unit will be omitted or the entire TVP, and VLP will be omitted.
 */
export type DoubleVerifyResponse = {
  IDS: number;
  ABS: string[];
  BSC: string[];
  TVP?: DoubleVerifySlotResponseMap;
  VLP?: DoubleVerifySlotResponseMap;
};

/**
 * The shape of the Double Verify specific slot targeting that will be set on each slot.
 */
export type DoubleVerifySlotTargeting = {
  tvp: string | "-1";
  vlp: string | "-1";
};

/**
 * Add DoubleVerify Targeting Values to PageTargeting type for use within the
 * DoubleVerifyPlugin Code.
 *
 * If any external plugins require access to this keyvalue, they will need to
 * import PageTargeting from this file.
 */
declare module "../../../../config/page" {
  interface PageTargeting {
    abs?: string[] | "-1";
    bsc?: string[] | "-1";
    ids?: string | "-1";
  }
}

export type { PageTargeting };
